import { Flex, Box, Text, RadioGroup, Radio } from "@chakra-ui/react";
import OutlineButton from "components/Button/OutlineButton";
import {
  RelatorioPrestacaoContasForm,
  TipoItemRelatorioPrestacaoContas,
  CapaOrientacaoEnum,
  RelatorioPrestacaoContasItem,
} from "core/features/relatorios/prestacao-contas/typings";
import { useRef } from "react";
import {
  Control,
  UseFormSetValue,
  FieldErrors,
  UseFormUnregister,
} from "react-hook-form";
import ItemConfiguracao, {
  ItemConfiguracaoProps,
} from "./components/ItemConfiguracao";
import colors from "resources/theme/colors";
import { TipoFiltroDataRelatorioPrestacaoContas } from "features/relatorios/prestacao-contas/typings";
import Field from "ui/components/Input/Field";
import { SortableElement } from "react-sortable-hoc";
import { SortableContainerComponent } from "components/SortableContainerComponent";
import { Row, Col } from "antd";
import moment from "moment";
import SelectMonthYear from "ui/components/Input/SelectMonthYear";
import arrayMove from "array-move";
import { showConfirm } from "infra/helpers/alerta";

interface FormConfiguracaoRelatorioProps {
  form: RelatorioPrestacaoContasForm;
  control: Control<RelatorioPrestacaoContasForm, any>;
  setValue: UseFormSetValue<RelatorioPrestacaoContasForm>;
  unregister: UseFormUnregister<RelatorioPrestacaoContasForm>;
  formErrors?: FieldErrors<RelatorioPrestacaoContasForm>;
  onVisualize?: (item: Partial<RelatorioPrestacaoContasItem>) => any;
  month?: string;
  showOnlyItems?: boolean;
  mode?: "download" | "save";
  onDeletarItem?: (itemId: string) => any;
}

export default function FormConfiguracaoRelatorio({
  form,
  control,
  setValue,
  unregister,
  formErrors,
  onVisualize,
  showOnlyItems,
  mode,
  onDeletarItem,
}: FormConfiguracaoRelatorioProps) {
  const idRef = useRef(1000);

  const isMesUnico = form.tipoPeriodo === "mes";

  const onRemove = async (index: number) => {
    const itemToRemove = form.items[index];

    if (itemToRemove) {
      unregister(`items.${index}`);
      unregister(`items.${index}.parametros`);
    }

    const copy = [...form.items];
    copy.splice(index, 1);
    setValue("items", copy);

    if (
      !itemToRemove.id ||
      (itemToRemove.id && Number.isFinite(Number(itemToRemove.id)))
    )
      return;

    if (mode === "download") {
      const { isConfirmed } = await showConfirm(
        "Deseja remover esse item no relatório principal?",
        {
          cancelButtonText: "Não",
          confirmButtonText: "Sim",
        }
      );

      if (isConfirmed) {
        setTimeout(() => {
          if (itemToRemove.id && !Number.isFinite(itemToRemove.id)) {
            onDeletarItem?.(itemToRemove.id);
          }
        }, 100);
      }
    }
  };

  const onAdd = () => {
    const id = `${idRef.current}`;
    idRef.current++;
    const lastItem = form.items?.[form.items.length - 1];

    const tipoItemToAdd =
      lastItem.tipoItem === TipoItemRelatorioPrestacaoContas.Capa
        ? TipoItemRelatorioPrestacaoContas.Relatorio
        : TipoItemRelatorioPrestacaoContas.Capa;

    const items = form.items.concat({
      id,
      tipoItem:
        lastItem.tipoItem === TipoItemRelatorioPrestacaoContas.Capa
          ? TipoItemRelatorioPrestacaoContas.Relatorio
          : TipoItemRelatorioPrestacaoContas.Capa,
      orientacao:
        tipoItemToAdd === TipoItemRelatorioPrestacaoContas.Capa
          ? CapaOrientacaoEnum.Retrato
          : undefined,
      tipoFiltroData: TipoFiltroDataRelatorioPrestacaoContas.AnoAtual,
    });

    setValue("items", items);
  };

  const onSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const copy = [...form.items];
    const sorted = arrayMove(copy, oldIndex, newIndex);

    setValue("items", sorted);
  };

  return (
    <Flex flexDir="column" position="relative">
      {!showOnlyItems ? (
        <Flex w="100%" flexDir="column" gridGap={4} mb={4}>
          <Field
            name="nome"
            rules={{
              required: true,
            }}
            label="Nome do relatório"
            control={control}
          />

          <Field name="observacao" label="Observação" control={control} />

          <Box as={Row} mb={4}>
            <Col sm={4}>
              <Field
                control={control}
                name="tipoPeriodo"
                label="Período"
                render={({ field, isInvalid }) => (
                  <RadioGroup
                    w="fit-content"
                    display="flex"
                    flexDir="column"
                    value={field.value}
                    onChange={(v) => {
                      const castValue =
                        v as RelatorioPrestacaoContasForm["tipoPeriodo"];

                      field.onChange(v);

                      if (castValue === "sempre" || castValue === "periodo") {
                        setValue("dataInicio", null);
                        setValue("dataFim", null);
                      }
                    }}
                  >
                    <Radio isInvalid={isInvalid} value={"sempre"} mb={4}>
                      Sempre
                    </Radio>
                    <Radio isInvalid={isInvalid} value={"mes"} mb={4}>
                      Mês único
                    </Radio>
                  </RadioGroup>
                )}
              />
            </Col>

            <Col sm={20} style={{ display: "flex", flexDirection: "column" }}>
              {isMesUnico ? (
                <Box as={Row} gutter={16} mb={-2} mt="auto">
                  <Col sm={12}>
                    <Field
                      shouldUnregister
                      control={control}
                      name="dataInicio"
                      label="Mês"
                      rules={{
                        required: isMesUnico,
                      }}
                      render={({ field }) => (
                        <SelectMonthYear
                          value={field.value ? moment(field.value) : undefined}
                          onChange={(val) => {
                            field.onChange(
                              val?.startOf("month").format("YYYY-MM-DD")
                            );

                            setValue(
                              "dataInicio",
                              val?.startOf("month").format("YYYY-MM-DD")
                            );
                            setValue(
                              "dataFim",
                              val?.endOf("month").format("YYYY-MM-DD")
                            );
                          }}
                          getPopupContainer={(triggerNode: any) =>
                            triggerNode.parentNode
                          }
                          style={{ width: "100%" }}
                        />
                      )}
                    />
                  </Col>
                </Box>
              ) : null}
            </Col>
          </Box>
        </Flex>
      ) : null}

      <Flex
        w="100%"
        bg="white"
        alignItems="center"
        pb={2}
        pt={2}
        position="sticky"
        top={-4}
        zIndex={10}
      >
        <Text fontWeight="500" fontSize={14}>
          Páginas do relatório
        </Text>
        <OutlineButton px={6} textAlign="center" onClick={onAdd} ml="auto">
          Adicionar outra página
        </OutlineButton>
      </Flex>

      <SortableContainerComponent onSortEnd={onSortEnd} useDragHandle>
        {form?.items?.map((item, index) => (
          <SortableItem
            draggable
            key={item.id}
            item={item}
            index={index}
            itemIndex={index}
            control={control}
            setValue={setValue}
            unregister={unregister}
            onVisualize={onVisualize}
            onRemove={index > 0 ? () => onRemove(index) : undefined}
            erroConfiguracao={
              !!formErrors?.items?.find((_, _index) => _index === index)
                ?.tipoFiltroData
            }
          />
        ))}
      </SortableContainerComponent>
    </Flex>
  );
}

const SortableItem = SortableElement((props: ItemConfiguracaoProps) => {
  return (
    <Box
      borderBottomWidth={1}
      borderBottomColor={colors.blueishGray50}
      py={2}
      zIndex={999999}
    >
      <ItemConfiguracao {...props} />
    </Box>
  );
});
